// src/services/firebaseServices.js
import {
    collection,
    addDoc,
    getDocs,
    doc,
    updateDoc,
    deleteDoc,
    query,
    orderBy,
    where,
    getDoc,
    limit
} from 'firebase/firestore';
import { db } from '../firebase';

// Properties Services

export const blogService = {
    // Get all posts
    async getAll() {
        try {
            const q = query(collection(db, 'posts'), orderBy('publishedAt', 'desc'));
            const querySnapshot = await getDocs(q);
            return querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
        } catch (error) {
            console.error('Error fetching posts:', error);
            throw error;
        }
    },

    // Get single post by slug
    async getBySlug(slug) {
        try {
            const q = query(
                collection(db, 'posts'),
                where('slug', '==', slug),
                limit(1)
            );
            const querySnapshot = await getDocs(q);

            if (!querySnapshot.empty) {
                const doc = querySnapshot.docs[0];
                return {
                    id: doc.id,
                    ...doc.data()
                };
            }
            return null;
        } catch (error) {
            console.error('Error getting post by slug:', error);
            throw error;
        }
    },

    // Add new post
    async add(postData) {
        try {
            const docRef = await addDoc(collection(db, 'posts'), {
                ...postData,
                createdAt: new Date(),
                updatedAt: new Date()
            });
            return { id: docRef.id, ...postData };
        } catch (error) {
            console.error('Error adding post:', error);
            throw error;
        }
    },

    // Update post
    async update(id, postData) {
        try {
            const postRef = doc(db, 'posts', id);
            await updateDoc(postRef, {
                ...postData,
                updatedAt: new Date()
            });
            return { id, ...postData };
        } catch (error) {
            console.error('Error updating post:', error);
            throw error;
        }
    },

    // Delete post
    async delete(id) {
        try {
            await deleteDoc(doc(db, 'posts', id));
            return id;
        } catch (error) {
            console.error('Error deleting post:', error);
            throw error;
        }
    }
};

export const propertiesService = {

    async getBySlug(slug) {
        try {
            const q = query(
                collection(db, 'properties'),
                where('slug', '==', slug),
                limit(1)
            );
            const querySnapshot = await getDocs(q);

            if (!querySnapshot.empty) {
                const doc = querySnapshot.docs[0];
                return {
                    id: doc.id,
                    ...doc.data()
                };
            }
            return null;
        } catch (error) {
            console.error('Error getting property by slug:', error);
            throw error;
        }
    },
    // Get all properties
    async getAll() {
        try {
            const q = query(collection(db, 'properties'), orderBy('createdAt', 'desc'));
            const querySnapshot = await getDocs(q);
            return querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
        } catch (error) {
            console.error('Error fetching properties:', error);
            throw error;
        }
    },

    async getById(id) {
        try {
            const docRef = doc(db, 'properties', id);
            const docSnap = await getDoc(docRef);

            if (docSnap.exists()) {
                return {
                    id: docSnap.id,
                    ...docSnap.data()
                };
            } else {
                return null;
            }
        } catch (error) {
            console.error('Error getting property:', error);
            throw error;
        }
    },

    // Get featured properties only
    async getFeatured() {
        try {
            const q = query(
                collection(db, 'properties'),
                where('featured', '==', true),
                orderBy('createdAt', 'desc')
            );
            const querySnapshot = await getDocs(q);
            return querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
        } catch (error) {
            console.error('Error fetching featured properties:', error);
            throw error;
        }
    },

    // Add new property
    async add(propertyData) {
        try {
            const docRef = await addDoc(collection(db, 'properties'), {
                ...propertyData,
                createdAt: new Date(),
                updatedAt: new Date()
            });
            return { id: docRef.id, ...propertyData };
        } catch (error) {
            console.error('Error adding property:', error);
            throw error;
        }
    },

    // Update property
    async update(id, propertyData) {
        try {
            console.log('🔄 PropertiesService: Starting update for property:', id);
            console.log('📄 PropertiesService: Data to update:', propertyData);
            console.log('🔐 PropertiesService: Database instance:', db ? 'Connected' : 'Not connected');

            if (!id) {
                throw new Error('Property ID is required for update');
            }

            const propertyRef = doc(db, 'properties', id);
            console.log('📍 PropertiesService: Document reference created:', propertyRef.path);

            const updateData = {
                ...propertyData,
                updatedAt: new Date()
            };

            console.log('📝 PropertiesService: Calling updateDoc with:', updateData);
            await updateDoc(propertyRef, updateData);

            console.log('✅ PropertiesService: updateDoc completed successfully');
            const result = { id, ...updateData };
            console.log('📤 PropertiesService: Returning result:', result);
            return result;
        } catch (error) {
            console.error('❌ PropertiesService: Error updating property:', error);
            console.error('❌ PropertiesService: Error code:', error.code);
            console.error('❌ PropertiesService: Error message:', error.message);
            console.error('❌ PropertiesService: Error stack:', error.stack);
            throw error;
        }
    },

    // Delete property
    async delete(id) {
        try {
            await deleteDoc(doc(db, 'properties', id));
            return id;
        } catch (error) {
            console.error('Error deleting property:', error);
            throw error;
        }
    }
};